import { BasicDetails } from './steps/BasicDetails';
import { NotificationMessage } from './steps/NotificationPreview';
import { PublishNotification } from './steps/PublishNotification';
import { modalUtils } from '@/app/core/utils';
import { useState, MutableRefObject, useRef, FC } from 'react';
import { isEmpty } from 'lodash';
import {
  ApiResponse,
  NotificationStepsInfo,
  Notification,
} from '@ssar-app/domain';
import { Modal, ProgressStep, StickyProgress, Spin } from '@ssar-app/uikit';
import { Flex, Text } from '@chakra-ui/react';
import { AiOutlineArrowRight, AiOutlineCheck } from 'react-icons/ai/index.js';

interface NotificationFormProps {
  onCancel: () => void;
  getNotifications: () => void;
  data: Notification;
  saveOrUpdateNotification: (
    Notification: Notification,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<ApiResponse<any>>;
}

export interface NotificationStepsProps {
  notification: Notification;
  setNotification: (notification: Notification) => void;
  stepOptions: ProgressStep[];
  setStepOptions: React.Dispatch<React.SetStateAction<ProgressStep[]>>;
  triggerStepValidation: MutableRefObject<(() => Promise<boolean>) | undefined>;
}

export const NotificationForm: FC<NotificationFormProps> = (props) => {
  const { onCancel, getNotifications, data, saveOrUpdateNotification } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  const [notification, setNotification] = useState<Notification>({
    ...data,
  });
  const [stepOptions, setStepOptions] = useState<Array<ProgressStep>>(
    NotificationStepsInfo,
  );
  const triggerStepValidation = useRef<() => Promise<boolean>>();

  const submitForm = () => {
    setIsLoading(true);
    saveOrUpdateNotification(notification)
      .then((response) => {
        if (response.resultCode === 0) {
          onCancel();
          modalUtils.showSuccessMessage(
            `Successfully ${
              isEmpty(notification.id) ? 'created' : 'updated'
            } Notification.`,
          );
          getNotifications();
        }
      })
      .catch(() =>
        modalUtils.showError(
          `Failed to ${
            isEmpty(notification.id) ? 'created' : 'updated'
          } Notification.`,
        ),
      )
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getFormStepDetails = () => {
    if (activeStep === 1) {
      return (
        <BasicDetails
          notification={notification}
          setNotification={setNotification}
          setStepOptions={setStepOptions}
          stepOptions={stepOptions}
          triggerStepValidation={triggerStepValidation}
        />
      );
    } else if (activeStep === 2) {
      return (
        <PublishNotification
          notification={notification}
          setNotification={setNotification}
          setStepOptions={setStepOptions}
          stepOptions={stepOptions}
          triggerStepValidation={triggerStepValidation}
        />
      );
    } else {
      return (
        <NotificationMessage
          notification={notification}
          setNotification={setNotification}
          setStepOptions={setStepOptions}
          stepOptions={stepOptions}
          triggerStepValidation={triggerStepValidation}
        />
      );
    }
  };

  return (
    <Modal
      isOpen
      header={
        <Text fontSize='xl' mb={0} fontWeight='semibold' textColor='blue.700'>
          {isEmpty(notification.id) ? 'Create' : 'Update'} Notification
        </Text>
      }
      contentProps={{ maxWidth: '5xl' }}
      scrollBehavior='outside'
      bodyProps={{ pt: 4, pb: 4 }}
      showFooterBtns={false}
      onClose={onCancel}
    >
      <Spin spinning={isLoading} tip='Submitting Notification Details'>
        <Flex direction='row' justifyContent='space-between'>
          <Flex w='full'>{getFormStepDetails()}</Flex>
          <StickyProgress
            steps={stepOptions}
            isCancel={true}
            secondaryCta=''
            cancelTitle='Are you sure want to cancel the Notification?'
            onCancelAction={onCancel}
            submittingStep={false}
            cancelModalProps={{
              contentProps: { maxH: 'unset' },
            }}
            containerProps={{
              border: '1px solid',
              borderTop: '2px solid',
              borderRight: '2px solid',
              borderColor: 'gray.300',
              borderRadius: 'lg',
              maxH: '310px',
              position: 'relative',
            }}
            stepProps={{
              _notLast: {
                borderBottom: '1px solid',
                borderBottomColor: 'gray.300',
              },
              as: 'span',
            }}
            mainButtonProps={{
              bg: 'primary',
            }}
            cancelButtonProps={{
              border: '1px solid',
              borderColor: 'gray.300',
            }}
            activeStep={activeStep}
            invalidStep={!!stepOptions[activeStep - 1]?.errorMessage}
            onMainAction={() => {
              triggerStepValidation.current &&
                triggerStepValidation
                  .current()
                  .then((val) =>
                    val
                      ? activeStep <= 2
                        ? setActiveStep(activeStep + 1)
                        : submitForm()
                      : null,
                  );
            }}
            onStepSelection={(id) => {
              triggerStepValidation.current &&
                triggerStepValidation
                  .current()
                  .then((val) =>
                    val
                      ? id === 3 && !notification.details.startTime
                        ? null
                        : setActiveStep(id)
                      : null,
                  );
            }}
            mainCta={
              activeStep <= 2
                ? activeStep <= 1
                  ? 'Continue to Publish Settings'
                  : 'Continue to Review & Submit'
                : notification.id
                ? 'Update Notification'
                : 'Submit Notification'
            }
            submitIcon={activeStep <= 2 ? AiOutlineArrowRight : AiOutlineCheck}
          />
        </Flex>
      </Spin>
    </Modal>
  );
};
